import React, { Component, ReactNode } from 'react';
import { Alert, Button, Container, Typography } from '@mui/material';
import log from 'loglevel';
import { isDevelopmentMode } from '@/utils/environmentIdentification';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface FileManagerErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  fallback?: (error: Error, resetErrorBoundary: () => void) => ReactNode;
}

/**
 * Error boundary specifically designed for the file manager feature.
 * Provides graceful error handling with reset functionality.
 */
export class FileManagerErrorBoundary extends Component<
  FileManagerErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: FileManagerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    log.error('FileManager ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default fallback UI
      return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert
            severity="error"
            action={
              <Button
                onClick={this.handleReset}
                size="small"
                variant="outlined"
              >
                Try Again
              </Button>
            }
          >
            <Typography variant="h6" gutterBottom>
              File Manager Error
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {this.state.error.message || 'An unexpected error occurred'}
            </Typography>
            {isDevelopmentMode() && this.state.errorInfo && (
              <details style={{ marginTop: '1rem' }}>
                <summary>Error Details (Development Only)</summary>
                <pre style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}
